import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Conversation {
  id: string;
  title: string;
  last_message_at: string;
  created_at: string;
}

export interface ChatLog {
  role: string;
  content: string;
  created_at: string;
  /** Traza del turno guardada por el backend: fuentes, herramientas, uso. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  context?: any;
}

export function useAssistantHistory() {
  const { user, profile } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [loadedMessages, setLoadedMessages] = useState<ChatLog[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Fetch recent conversations (only those with at least one assistant reply)
  const fetchConversations = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [convRes, assistantLogsRes] = await Promise.all([
        supabase
          .from("assistant_conversations")
          .select("id, title, last_message_at, created_at")
          .eq("user_id", user.id)
          .order("last_message_at", { ascending: false })
          .limit(20),
        supabase
          .from("assistant_chat_logs")
          .select("conversation_id")
          .eq("user_id", user.id)
          .eq("role", "assistant")
          .not("conversation_id", "is", null)
          .limit(1000),
      ]);

      const validIds = new Set<string>(
        ((assistantLogsRes.data as Array<{ conversation_id: string | null }>) || [])
          .map((r) => r.conversation_id)
          .filter((id): id is string => !!id)
      );

      const filtered = ((convRes.data as Conversation[]) || []).filter((c) =>
        validIds.has(c.id)
      );
      setConversations(filtered.slice(0, 10));
    } catch (err) {
      console.error("Error fetching conversations:", err);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Load messages for a conversation
  const loadConversation = useCallback(async (conversationId: string) => {
    setIsLoadingHistory(true);
    setActiveConversationId(conversationId);
    try {
      const { data } = await supabase
        .from("assistant_chat_logs")
        .select("role, content, created_at, context")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true })
        .limit(100);
      setLoadedMessages((data as ChatLog[]) || []);
    } catch (err) {
      console.error("Error loading conversation:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Create a new conversation
  const createConversation = useCallback(async (firstMessage: string): Promise<string | null> => {
    if (!user?.id) return null;
    const title = firstMessage.slice(0, 60) + (firstMessage.length > 60 ? "..." : "");
    try {
      const { data, error } = await supabase
        .from("assistant_conversations")
        .insert({
          user_id: user.id,
          company_id: profile?.company_id || null,
          title,
        })
        .select("id")
        .single();
      if (error) throw error;
      const id = (data as any)?.id;
      setActiveConversationId(id);
      // Don't refresh list yet — only show after first assistant reply lands
      return id;
    } catch (err) {
      console.error("Error creating conversation:", err);
      return null;
    }
  }, [user?.id, profile?.company_id, fetchConversations]);

  // Save a message to the log (context opcional: traza/fuentes del turno)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const saveMessage = useCallback(async (conversationId: string, role: string, content: string, context?: any) => {
    if (!user?.id) return;
    try {
      await supabase.from("assistant_chat_logs").insert({
        user_id: user.id,
        company_id: profile?.company_id || null,
        conversation_id: conversationId,
        role,
        content,
        ...(context ? { context } : {}),
      });
      // Update last_message_at
      await supabase
        .from("assistant_conversations")
        .update({ last_message_at: new Date().toISOString() })
        .eq("id", conversationId);
      // Refresh list once an assistant reply is saved (so it becomes visible)
      if (role === "assistant") {
        fetchConversations();
      }
    } catch (err) {
      console.error("Error saving message:", err);
    }
  }, [user?.id, profile?.company_id, fetchConversations]);

  // Start a new conversation (clear state)
  const startNewConversation = useCallback(() => {
    setActiveConversationId(null);
    setLoadedMessages([]);
  }, []);

  // Delete a conversation
  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      await supabase
        .from("assistant_conversations")
        .delete()
        .eq("id", conversationId);
      if (activeConversationId === conversationId) {
        startNewConversation();
      }
      fetchConversations();
    } catch (err) {
      console.error("Error deleting conversation:", err);
    }
  }, [activeConversationId, startNewConversation, fetchConversations]);

  return {
    conversations,
    activeConversationId,
    loadedMessages,
    isLoadingHistory,
    fetchConversations,
    loadConversation,
    createConversation,
    saveMessage,
    startNewConversation,
    deleteConversation,
  };
}
